<script lang="ts">
import { computed, defineComponent, ref, watch } from "vue";
import formatUtils from "@/businesslogic/utils/formatUtils";
import IconSprite from "@/components/IconSprite.vue";
import Logger from "js-logger";
import { inputChangeHandler } from "@/businesslogic/utils/events";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "VHV2024FahrradModul",
  methods: { inputChangeHandler },
  components: { IconSprite },
  setup() {
    const modulInfo = ref(false);
    const piniaStore = useFormStore();
    const { formatCurrency } = formatUtils();

    const beitrag = computed(() => {
      const preis = calculateIndex("VHV2024_FAHRRADDIEBSTAHL");
      return preis !== undefined && preis > 0 ? formatCurrency(preis) : "inklusive";
    });

    const istPremiumTarif = computed(() => {
      Logger.debug(
        "makler und Premium",
        piniaStore.state.isMakler,
        " ",
        piniaStore.state.tarifinformationen.produktLinie,
      );
      return (
        (piniaStore.state.isMakler || piniaStore.state.isMaklerLink) &&
        piniaStore.state.tarifinformationen.produktLinie === "003"
      );
    });

    function calculateIndex(preisId: string) {
      for (const modul of piniaStore.state.modulPreise) {
        if (modul.preisName == preisId) {
          return modul.preis;
        }
      }
    }

    function zeigeInfo() {
      modulInfo.value = !modulInfo.value;
    }

    watch(
      () => piniaStore.state.tarifinformationen.produktLinie,
      () => {
        piniaStore.state.tarifinformationen.fahrradschutz = istPremiumTarif.value
          ? true
          : piniaStore.state.tarifinformationen.fahrradschutz;
      },
    );

    watch(
      () => piniaStore.state.tarifinformationen.vemaKonzept,
      () => {
        if (
          piniaStore.state.tarifinformationen.vemaKonzept &&
          piniaStore.state.tarifinformationen.fahrradschutzBetrag < 10000
        ) {
          piniaStore.state.tarifinformationen.fahrradschutzBetrag = 10000;
        }
      },
    );

    return {
      piniaStore,
      modulInfo,
      formatCurrency,
      zeigeInfo,
      calculateIndex,
      beitrag,
      istPremiumTarif,
    };
  },
});
</script>

<template>
  <div>
    <div class="o-grid u-mar-t-2">
      <div class="o-grid__cell o-grid__cell--12 o-grid__cell--3-480 u-text-align-right--480 u-form-grid-nospace-y">
        <h2 class="u-form-heading unset-text-align">Zusätzlich absichern</h2>
      </div>
      <div
        class="o-grid__cell o-grid__cell--12 o-grid__cell--9-480 o-grid__cell--8-768 o-grid__cell--7-1024 o-grid__cell--6-1200 u-form-grid-lowspace-y"
      >
        <div
          class="gev19-optional-module"
          v-bind:class="{
            'gev19-optional-module--enabled': piniaStore.state.tarifinformationen.fahrradschutz,
          }"
        >
          <div>
            <div
              :class="{
                'c-form-control-checkbox--disabled': istPremiumTarif,
              }"
              class="c-form-control-checkbox"
            >
              <input
                id="fahrradschutz"
                v-model="piniaStore.state.tarifinformationen.fahrradschutz"
                :disabled="istPremiumTarif"
                class="c-form-control-checkbox__input"
                data-cy="fahrradschutzOption"
                type="checkbox"
                aria-label="Fahrradschutz Auswahl"
              />
              <label class="c-form-control-checkbox__label" for="fahrradschutz">
                <icon-sprite
                  :ist-klein="true"
                  css-klassen="c-form-control-checkbox__icon"
                  icon-name="icon-check-mark"
                />
                <span v-if="!piniaStore.state.isMakler" class="c-form-control-checkbox__label-text">
                  Fahrraddiebstahl (Wert Ihrer Fahrräder)
                </span>
                <span v-else class="c-form-control-checkbox__label-text"> Fahrraddiebstahl </span>
              </label>
              <span class="gev19__contexthelp-open" title="Hilfe anzeigen" @click="zeigeInfo()"> </span>
            </div>
            <div class="gev19-optional-module-content">
              <span class="gev19-optional-module-content-icon">
                <icon-sprite :ist-gross="true" icon-name="icon-bike" />
              </span>
              <div class="gev19-optional-module-content-text" style="display: inline">
                <ul class="c-list">
                  <li class="c-list__item">24h Diebstahlschutz</li>
                  <li class="c-list__item">Schlosssicherung genügt</li>
                  <li v-if="piniaStore.state.isMakler" class="c-list__item c-list__item_icon-present">
                    1.000 € sind bereits im Premium-Tarif enthalten
                  </li>
                </ul>
              </div>
            </div>
            <div class="o-grid gev19-optional-module-subform">
              <div class="o-grid__cell o-grid__cell--12">
                <div class="c-form-control">
                  <select
                    v-model.number="piniaStore.state.tarifinformationen.fahrradschutzBetrag"
                    :disabled="!piniaStore.state.tarifinformationen.fahrradschutz"
                    class="chooseOptionsSelect c-form-control-select"
                    data-cy="fahrradschutzBetrag"
                    name="fahrradschutzBetrag"
                    aria-label="Wert Fahrrad"
                    @change="$emit('change', inputChangeHandler($event))"
                  >
                    <option :value="1000">1.000 €</option>
                    <option :value="2000">2.000 €</option>
                    <option :value="3000">3.000 €</option>
                    <option :value="4000">4.000 €</option>
                    <option :value="5000">5.000 €</option>
                    <option :value="6000">6.000 €</option>
                    <option :value="7000">7.000 €</option>
                    <option :value="8000">8.000 €</option>
                    <option :value="9000">9.000 €</option>
                    <option :value="10000">10.000 €</option>
                    <option :value="11000">11.000 €</option>
                    <option :value="12000">12.000 €</option>
                    <option :value="13000">13.000 €</option>
                    <option :value="14000">14.000 €</option>
                    <option :value="15000">15.000 €</option>
                    <option :value="16000">16.000 €</option>
                    <option :value="17000">17.000 €</option>
                    <option :value="18000">18.000 €</option>
                    <option :value="19000">19.000 €</option>
                    <option :value="20000">20.000 €</option>
                  </select>
                </div>
              </div>
            </div>
            <div class="gev19-optional-module-premium u-text-align-right">{{ beitrag }}</div>
          </div>
          <div v-if="modulInfo" class="gev19__contexthelp-text" @click="zeigeInfo()">
            <span v-if="!piniaStore.state.isMakler">
              Mit der Fahrraddiebstahldeckung erhalten Sie einen Rund-um-die-Uhr-Diebstahlschutz für Ihre Fahrräder. Wir
              erwarten lediglich, dass Sie die Räder bei Nichtgebrauch mit einem verkehrsüblichen Fahrradschloss gegen
              Diebstahl sichern. Haben Sie mehrere Fahrräder, geben Sie für die Beitragsberechnung nur den Wert des
              teuersten Fahrrads an, sofern Sie alle Fahrräder einzeln mit einem Fahrradschloss sichern.
            </span>
            <span v-else>
              Mit der Fahrraddiebstahldeckung erhält Ihr Kunde einen Rund-um-die-Uhr-Diebstahlschutz für seine
              Fahrräder. Die Räder sind bei Nichtgebrauch mit einem verkehrsüblichen Fahrradschloss gegen Diebstahl
              sichern. Hat Ihr Kunde mehrere Fahrräder, geben Sie für die Beitragsberechnung nur den Wert des teuersten
              Fahrrads an, sofern alle Fahrräder einzeln mit einem Fahrradschloss gesichert werden.<br /><br />Im Tarif
              Premium sind Fahrräder bereits mit 1.000 € beitragsfrei mitversichert.
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
